import React, { useEffect } from 'react';
import {
  Box,
  Typography,
  Icons,
  IconButton,
  Chip,
} from '@ublend-npm/aulaui-core';
import { useDispatch, useSelector } from 'react-redux';
import { fetchHomePosts } from '../../../../../actions/home/posts';
import InfiniteScroller from '../../../../containers/common/InfiniteScroller';
import { useTheme, useMediaQuery } from '@ublend-npm/aulaui-core';
import { Post } from '@app/types/core';
import Switch from '@app/components/presentational/common/Switch';
import RecentUpdatesSkeleton from './RecentUpdatesSkeleton';
import RecentUpdateCard from './RecentUpdateCard';
import RecentUpdatesInfoPopup from './RecentUpdatesInfoPopup';
import PostDetailPopup from './PostDetailPopup';

const RecentUpdates = () => {
  const dispatch = useDispatch();
  const [isInfoOpen, setIsInfoOpen] = React.useState(false);
  const [selectedPost, setSelectedPost] = React.useState<any>(null);

  const { posts, postsLoading, postsHasMore, studentPostOnly } = useSelector(
    (state: any) => state.home || {}
  );

  const theme = useTheme();
  const isAboveSmallScreen = useMediaQuery(theme.breakpoints.up('sm'));

  useEffect(() => {
    // Only fetch initially if empty and has more
    if (posts.length === 0 && postsHasMore && !postsLoading) {
      dispatch(fetchHomePosts(true, studentPostOnly) as any);
    }
  }, [dispatch, posts.length, postsHasMore, postsLoading, studentPostOnly]);

  const loadMorePosts = () => {
    dispatch(fetchHomePosts(false, studentPostOnly) as any);
  };
  return (
    <Box
      component="section"
      aria-labelledby="recent-updates-title"
      sx={{
        pt: 3,
        pb: 2,
        pr: 2,
        pl: 1.5,
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      <Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: 1,
            mb: 0.5,
          }}
        >
          <Typography
            variant="subtitle1"
            fontWeight="medium"
            color="primary"
            id="recent-updates-title"
          >
            Recent updates
          </Typography>
          <IconButton
            onClick={() => setIsInfoOpen(true)}
            aria-label="Recent updates information"
            size="small"
            sx={{
              color: theme.palette.text.secondary,
              padding: 0.5,
              '&:hover': {
                color: theme.palette.primary.main,
              },
            }}
          >
            <Icons.InfoOutlined fontSize="small" />
          </IconButton>
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'flex-start',
            alignItems: 'center',
          }}
        >
          <Typography
            color="textPrimary"
            variant="body1"
            id="students-only-label"
          >
            Show students post only
          </Typography>
          <Switch
            checked={!!studentPostOnly}
            onChange={() =>
              dispatch(fetchHomePosts(true, !studentPostOnly) as any)
            }
            inputProps={{
              id: 'students-only-switch',
              'aria-labelledby': 'students-only-label',
            }}
          />
        </Box>
      </Box>
      {posts.length === 0 && postsLoading ? (
        <RecentUpdatesSkeleton
          orientation={isAboveSmallScreen ? 'vertical' : 'horizontal'}
        />
      ) : posts.length === 0 ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: isAboveSmallScreen ? 'column' : 'row',
            gap: 2,
            height: isAboveSmallScreen ? 'auto' : '365px',
            overflowX: isAboveSmallScreen ? 'visible' : 'auto',
            pb: isAboveSmallScreen ? 0 : 2,
          }}
        >
          <Box
            sx={{
              borderRadius: 6,
              padding: 2.5,
              border: `1px solid ${theme.palette.divider}`,
              bgcolor: theme.palette.mode === 'dark' ? '#363636' : '#FFFFFF',
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
              height: '100%',
              width: isAboveSmallScreen ? '100%' : '320px',
              flexShrink: 0,
              boxSizing: 'border-box',
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
              <Chip
                label="Announcements"
                size="small"
                sx={{
                  bgcolor:
                    theme.palette.mode === 'dark' ? '#4a4a4a' : '#eaeaea',
                  color: theme.palette.text.secondary,
                  fontWeight: 500,
                  fontSize: '12px',
                }}
              />
            </Box>
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                bgcolor: theme.palette.mode === 'dark' ? '#2b2b2b' : '#f8f8fa',
                borderRadius: 4,
                padding: 3,
                textAlign: 'center',
                gap: 1,
                minHeight: '180px',
              }}
            >
              <Typography variant="subtitle1" color="textPrimary">
                No updates yet
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Updates from your spaces will appear here.
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              borderRadius: 6,
              padding: 2.5,
              border: `1px solid ${theme.palette.divider}`,
              bgcolor: theme.palette.mode === 'dark' ? '#363636' : '#FFFFFF',
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
              height: '100%',
              width: isAboveSmallScreen ? '100%' : '320px',
              flexShrink: 0,
              boxSizing: 'border-box',
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
              <Chip
                label="Common room"
                size="small"
                sx={{
                  bgcolor:
                    theme.palette.mode === 'dark' ? '#4a4a4a' : '#eaeaea',
                  color: theme.palette.text.secondary,
                  fontWeight: 500,
                  fontSize: '12px',
                }}
              />
            </Box>
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                bgcolor: theme.palette.mode === 'dark' ? '#2b2b2b' : '#f8f8fa',
                borderRadius: 4,
                padding: 3,
                textAlign: 'center',
                gap: 1,
                minHeight: '180px',
              }}
            >
              <Typography variant="subtitle1" color="textPrimary">
                No discussion yet
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Start a conversation or wait for others to post.
              </Typography>
            </Box>
          </Box>
        </Box>
      ) : (
        <InfiniteScroller
          items={posts}
          loading={postsLoading}
          hasMore={postsHasMore}
          loadMore={loadMorePosts}
          orientation={isAboveSmallScreen ? 'vertical' : 'horizontal'}
          height={isAboveSmallScreen ? '900px' : '365px'}
          renderItem={(item: Post | any) => (
            <Box
              role="listitem"
              sx={{
                height: isAboveSmallScreen ? 'auto' : '100%',
                width: isAboveSmallScreen ? 'auto' : '320px',
                mb: 2,
                mr: 2,
              }}
            >
              <RecentUpdateCard
                key={item?.objectId || item?.id}
                item={item}
                onReadMore={(post) => setSelectedPost(post)}
              />
            </Box>
          )}
        />
      )}
      <RecentUpdatesInfoPopup
        open={isInfoOpen}
        onClose={() => setIsInfoOpen(false)}
      />
      <PostDetailPopup
        item={selectedPost}
        open={!!selectedPost}
        onClose={() => setSelectedPost(null)}
      />
    </Box>
  );
};

export default RecentUpdates;
